import { useEffect, useRef, useState } from "react";
import { DataGrid, Column } from "devextreme-react/data-grid";
import IconButton from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import { Button as DxButton } from "devextreme-react/button";
import { confirm } from "devextreme/ui/dialog";

import { ChangesDetail, HQSiteChangesDetailDTO, HQSiteChangesDTO } from "../../../api-client-nswag/taxportal-client";
import { upgradeSiteClientApi } from "../../../api-client-nswag/taxportal-client-runtime";
import { executeDownload } from "../../../util/Utils";
import { SnackbarMessage, useSnackbar } from "../../../hooks";

import { ModalReviewChanges } from "./ModalReviewChanges";
import { Modal } from "../Modal/Modal";

import { ModalSitesChangesProps, SelectedChangeData, changesDetailData, constants } from "./UpgradeSitesGrids.types";

type DatagridRefType = DataGrid;
type ModalSiteChangesDetailProps = ModalSitesChangesProps & {
  hqSiteId?: number;
  userEmail?: string;
};

const { DETAILS_TITLE, FILE_SITE_UPGRADE_NAME, EXCEL_FILE_EXTENSION } = constants;
const initialStateData = { pending: true, changes: [] };

export const ModalSiteChangesDetail = ({
  handleClose,
  isDisplayModal,
  idUpgradeSiteTracks,
  hqSiteId,
  userEmail,
}: ModalSiteChangesDetailProps) => {
  const detailsGridRef = useRef<DatagridRefType>(null);
  const { showSnackbar } = useSnackbar();
  const [changesDetailData, setChangesDetailData] = useState<changesDetailData>(initialStateData);
  const [isDisplayDetailsModal, setIsDisplayDetailsModal] = useState(false);
  const [selectedChange, setSelectedChange] = useState<SelectedChangeData>();
  const [excelData, setExcelData] = useState<HQSiteChangesDTO>();
  const [isRetryDisabled, setIsRetryDisabled] = useState<boolean>(false);

  const fetchData = async () => {
    try {
      if (!idUpgradeSiteTracks) {
        setChangesDetailData({ pending: false, changes: [] });
        return;
      }
      setChangesDetailData(initialStateData);
      const changes = await upgradeSiteClientApi.highQ_GetSitesUpgradeDetailStatus(String(idUpgradeSiteTracks));
      setExcelData(changes);
      const { siteChanges } = changes;
      setChangesDetailData({ pending: false, changes: siteChanges });
    } catch (error) {}
  };

  const handleSeeDetailsButton = (data: HQSiteChangesDetailDTO, changeDetail: ChangesDetail) => {
    setSelectedChange({ ...data, detailSelected: changeDetail });
    setIsDisplayDetailsModal(true);
  };

  const handleExportButton = async () => {
    if (!excelData) return;
    try {
      showSnackbar({ message: SnackbarMessage.DOWNLOAD_MESSAGE });
      const base64String = await upgradeSiteClientApi.HQTemplateSiteExcelExport(excelData);
      if (base64String)
        executeDownload(base64String, String(excelData!.siteId), FILE_SITE_UPGRADE_NAME, EXCEL_FILE_EXTENSION);
    } catch (error) {
      showSnackbar({
        message: SnackbarMessage.DOWNLOAD_FAILED.replace("%site%", String(excelData!.siteId)) as SnackbarMessage,
        severity: "error",
      });
    }
  };

  const handleCloseDetailsModal = () => {
    setIsDisplayDetailsModal(false);
  };

  const handleRetry = async (comparisonId: string) => {
    if (!hqSiteId || !comparisonId || !userEmail) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
      return;
    }

    const result = await confirm("Are you sure you want to continue?", "Please Confirm");
    if (!result) return;

    try {
      setIsRetryDisabled(true);
      await upgradeSiteClientApi.RetrySiteUpgradeDetailComparison(hqSiteId, comparisonId, userEmail);
      fetchData();
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
    } finally {
      setIsRetryDisabled(false);
    }
  };

  const renderRetry = ({ row: { data } }: any) => {
    const status = String(data?.status || "").trim().toUpperCase();

    if (!data?.status || !data?.comparisonId || status === "SUCCESS") return null;
    return (
      <DxButton
        onClick={() => handleRetry(data.comparisonId)}
        icon="refresh"
        className="hq-icon-button"
        disabled={isRetryDisabled}
      />
    );
  };

  const renderDetails = ({ row: { data } }: any) => {
    if (!data.changesDetail) return;
    return data.changesDetail.map((detail: ChangesDetail) => (
      <div className="changeRow">
        <div className="change">{detail.change}</div>

        <div className="more-info">
          <IconButton aria-label="More Info" title="More Info" onClick={() => handleSeeDetailsButton(data, detail)}>
            <SearchIcon />
          </IconButton>
        </div>
      </div>
    ));
  };

  useEffect(() => {
    fetchData();
  }, [idUpgradeSiteTracks]);

  return (
    <Modal
      title={DETAILS_TITLE}
      maxWidth="xl"
      btnOpenText="Export to Excel"
      handleContinue={handleExportButton}
      btnCloseText="Cancel"
      isDisplayModal={isDisplayModal}
      handleClose={handleClose}
    >
      <>
        <DataGrid
          id="grid-modal-changes"
          ref={detailsGridRef}
          keyExpr="comparisonId"
          allowColumnResizing
          dataSource={changesDetailData.changes}
          noDataText={changesDetailData.pending ? "Loading..." : "No data"}
          showBorders
          wordWrapEnabled
        >
          <Column dataField="module" width="150px" />
          <Column dataField="change" />
          <Column dataField="object" />
          <Column dataField="details" cellRender={renderDetails} caption="Changes detail" />
          <Column dataField="status" width="100px" />
          <Column cellRender={renderRetry} type="buttons" caption="Retry" width="110px" />
        </DataGrid>

        <ModalReviewChanges
          selectedChangesDetails={selectedChange!}
          isDisplayModal={isDisplayDetailsModal}
          handleClose={handleCloseDetailsModal}
          isReviewOnly
          isBatchUpgrade
        />
      </>
    </Modal>
  );
};
